import React, { useRef, useState, useEffect } from 'react';
import { CircleNotch, SpeakerHigh, X } from '@phosphor-icons/react';
import { Howl } from 'howler';
import { Button } from '../../Utility/Button';
import { RootState, useAppSelector } from '../../../store';

type AssistantAudioControlsProps = {
    identifier: string;
    text: string;
    isSending: boolean;
    variant: 'desktop' | 'mobile';
};

const AssistantAudioControls: React.FC<AssistantAudioControlsProps> = ({ identifier, text, isSending, variant }) => {
    const [audioIsLoading, setAudioIsLoading] = useState(false);
    const [audioIsPlaying, setAudioIsPlaying] = useState(false);
    const howlRef = useRef<Howl | null>(null);
    const audioUrlRef = useRef<string | null>(null);

    const settings = useAppSelector((state: RootState) => state.settings);
    const hasTtsModel = settings?.ttsModel && settings.ttsModel.trim() !== '';

    const stopAudio = () => {
        if (howlRef.current) {
            try {
                howlRef.current.stop();
                howlRef.current.unload();
            } catch (error) {
                // Error stopping Howl instance
            }
            howlRef.current = null;
        }

        if (audioUrlRef.current) {
            URL.revokeObjectURL(audioUrlRef.current);
            audioUrlRef.current = null;
        }

        setAudioIsPlaying(false);
        setAudioIsLoading(false);
    };

    const playAudio = async () => {
        if (audioIsPlaying) {
            stopAudio();
            return;
        }

        if (audioIsLoading) {
            return;
        }

        try {
            setAudioIsLoading(true);

            if (howlRef.current) {
                try {
                    howlRef.current.stop();
                    howlRef.current.unload();
                } catch (e) {
                    // Error unloading previous Howl instance
                }
                howlRef.current = null;
            }
            if (audioUrlRef.current) {
                URL.revokeObjectURL(audioUrlRef.current);
                audioUrlRef.current = null;
            }
            const apiBaseUrl = settings.apiBaseUrl || '';

            const audioUrl = `${apiBaseUrl}/voice/play?identifier=${encodeURIComponent(identifier)}`;

            const howl = new Howl({
                src: [audioUrl],
                html5: true,
                format: ['mp3'],
                // @ts-ignore - xhrWithCredentials is supported by Howler.js but not in type definitions
                xhrWithCredentials: true, // Required for Safari to send cookies
                onload: () => {
                    setAudioIsLoading(false);
                },
                onloaderror: (_id: number, error: unknown) => {
                    setAudioIsLoading(false);
                    setAudioIsPlaying(false);
                },
                onplay: () => {
                    setAudioIsPlaying(true);
                    setAudioIsLoading(false);
                },
                onpause: () => {
                    setAudioIsPlaying(false);
                },
                onend: () => {
                    setAudioIsPlaying(false);
                    if (howlRef.current) {
                        howlRef.current.unload();
                        howlRef.current = null;
                    }
                },
                onstop: () => {
                    setAudioIsPlaying(false);
                },
                onplayerror: (_id: number, error: unknown) => {
                    setAudioIsLoading(false);
                    setAudioIsPlaying(false);
                },
            });

            howlRef.current = howl;

            const soundId = howl.play();
            if (!soundId) {
                setAudioIsLoading(false);
                setAudioIsPlaying(false);
            }
        } catch (error) {
            setAudioIsLoading(false);
            setAudioIsPlaying(false);
            if (howlRef.current) {
                howlRef.current.unload();
                howlRef.current = null;
            }
        }
    };

    useEffect(() => {
        return () => {
            stopAudio();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleClick = () => {
        if (audioIsPlaying) {
            stopAudio();
        } else {
            void playAudio();
        }
    };

    if (!hasTtsModel) {
        return null;
    }

    if (variant === 'desktop') {
        return (
            <span className="text-chat-editIconColor inline-block mr-3" onClick={handleClick}>
                <Button variant="secondaryRound" className="cursor-pointer h-[26px] w-[26px] md:h-[26px] md:w-[26px]">
                    {audioIsLoading ? (
                        <CircleNotch size={16} className="inline animate-spin" />
                    ) : audioIsPlaying ? (
                        <X size={16} className="inline" />
                    ) : (
                        <SpeakerHigh size={16} className="inline" />
                    )}
                </Button>
            </span>
        );
    }

    if (variant === 'mobile') {
        if (isSending) {
            return null;
        }

        return (
            <span className="sm:hidden text-chat-editIconColor inline-block" onClick={handleClick}>
                <Button variant="secondaryRound" className="cursor-pointer h-[26px] w-[26px]">
                    {audioIsLoading ? (
                        <CircleNotch size={16} className="inline animate-spin" />
                    ) : audioIsPlaying ? (
                        <X size={16} className="inline" />
                    ) : (
                        <SpeakerHigh size={16} className="inline" />
                    )}
                </Button>
            </span>
        );
    }

    return null;
};

export default AssistantAudioControls;
